import React, { useState } from "react";
import { CloseOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import DataTable from "components/DataTable";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import { PRODUCT_CATEGORIES, stations_location } from "constants/order";
import { Button, Popconfirm, Space, Tooltip, Tag } from "antd";
import { CustomerText, DateText, PriceText, SelectText } from "components/DataTableText";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OrderService from "services/order";
import PrintOrderButton from "components/PrintOrderButton";
import "./OrderActions.scss";

const GIFT_PRODUCT_CATEGORIES = [2, 3, 4];
const GIFT_CATEGORY_COLOR = {
  2: "blue",   // Zalo
  3: "green",  // SMS
  4: "purple", // Email
};
const GIFT_ORDER_STATUS_COLOR = {
  0: "warning",     // Đang chờ
  10: "processing", // Đã xác nhận
  20: "error",      // Đã huỷ
  30: "success",    // Hoàn thành
};
const GiftOrderStatusCell = ({ value, options, translation, id, refetch }) => {
  const [valueOrder, setValueOrder] = useState(value);
  const status = stations_location.find((item) => item.value === valueOrder);
  const statusText = translation(status?.label) || translation("orderStatus.pending");

  return (
    <SelectText
      defaultValue={valueOrder}
      options={options}
      onUpdate={async (newStatus) => {
        await OrderService.updateOrder({
          id,
          data: {
            orderStatus: newStatus,
            note: " ",
          },
        });
        setValueOrder(newStatus);
        refetch();
      }}
      textClass=""
      textValue={
        <Tag color={GIFT_ORDER_STATUS_COLOR[valueOrder] || "default"}>
          {statusText}
        </Tag>
      }
    />
  );
};
const GiftOrderTable = ({
  data,
  loading,
  pagination,
  filters,
  sorter,
  handleTableChange,
  hasMore,
  refetch,
  handleSearch,
  startDate,
  endDate,
  onCreateGift,
  actionButtons = [],
  columnStorageKey,
}) => {
  const history = useHistory();
  const { t: translation } = useTranslation();
  const orderStatusOptions = stations_location.map((item) => ({
    label: translation(item.label),
    value: item.value,
  }));
  const tableData = [...(data || [])].map((item) => ({
  ...item,
  giftAmount: item.orderItems?.reduce((sum, orderItem) => {
    return sum + Number(orderItem.payAmount || 0);
  }, 0) || 0,
}));

if (sorter?.key === "totalAmount") {
  const direction = sorter?.value === "asc" ? 1 : -1;

  tableData.sort((a, b) => {
    return (a.giftAmount - b.giftAmount) * direction;
  });
}
  const columns = [
    {
      title: "ID",
      dataIndex: "orderId",
      sortKey: "orderId",
      width: 90,
      align: "center",
      render: (orderId) => (
        <span
          className="order-index"
          onClick={() => history.push(`/order-detail/${orderId}`, { backTab: "5" })}
        >
          {orderId}
        </span>
      ),
    },
    {
      title: "Tổng tiền",
      dataIndex: "giftAmount",
      sortKey: "totalAmount",
      width: 130,
      align: "center",
      render: (giftAmount) => {
        return <PriceText className="order-amount" price={giftAmount || 0} />;
      },
    },
    {
      title: "Khách hàng",
      dataIndex: "customer",
      sortKey: "firstName",
      width: 150,
      align: "center",
      render: (_, record) => (
        <CustomerText
          firstName={record?.firstName}
          phoneNumber={record?.phoneNumber}
          email={record?.email}
        />
      ),
    },
    {
      title: "Loại quà tặng",
      dataIndex: "productCategory",
      sortKey: "productCategory",
      width: 160,
      align: "center",
      showOnBar: true,
      filterType: "select",
      filterOptions: GIFT_PRODUCT_CATEGORIES.map((category) => ({
        value: category,
        label: PRODUCT_CATEGORIES[category]?.label || category,
      })),
      render: (_, record) => {
        const productCategory = record.orderItems?.[0]?.productCategory;
        const label = PRODUCT_CATEGORIES[productCategory]?.label || "-";

        return (
          <Tag color={GIFT_CATEGORY_COLOR[productCategory] || "default"}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: "Tên quà tặng",
      dataIndex: "giftPackage",
      sortKey: "orderItemName",
      width: 220,
      align: "center",
      render: (_, record) => record.orderItems?.map((item) => item.orderItemName).filter(Boolean).join(", ") || "-",
    },
    {
      title: "Trạng thái",
      dataIndex: "orderStatus",
      sortable: false,
      width: 120,
      align: "center",
      render: (orderStatus, record) => (
        <GiftOrderStatusCell
          value={orderStatus}
          options={orderStatusOptions}
          translation={translation}
          id={record?.orderId}
          refetch={refetch}
        />
      ),
    },
    {
      title: "Thời gian tạo",
      dataIndex: "createdAt",
      sortKey: "createdAt",
      width: 150,
      align: "center",
      filterType: "date",
      render: (createdAt) => <DateText className="order-date" date={createdAt} />,
    },
    {
      title: "Hành động",
      dataIndex: "action",
      sortable: false,
      width: 180,
      align: "left",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem chi tiết" placement="top">
            <Button
              type="text"
              icon={<EyeOutlined className="action-icon view" />}
              className="action-button"
              onClick={() => history.push(`/order-detail/${record?.orderId}`, { backTab: "5" })}
            />
          </Tooltip>
          <PrintOrderButton
            orderId={record?.orderId}
            showText={false}
            buttonType="text"
          />
          {record?.orderStatus !== 20 && (
            <Popconfirm
              title="Bạn có chắc chắn muốn huỷ đơn hàng này?"
              onConfirm={async () => {
                await OrderService.updateOrder({
                  id: record?.orderId,
                  data: {
                    orderStatus: 20,
                    note: " ",
                  },
                });
                refetch();
              }}
              okText="Có"
              cancelText="Không"
            >
              <Tooltip title={translation("order.cancel")} placement="top">
                <Button
                  type="text"
                  icon={<CloseOutlined className="action-icon cancel" />}
                  className="action-button"
                  danger
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];
  const giftSortOptions = [
    { label: "ID", value: "orderId" },
    { label: "Giá trị", value: "totalAmount" },
    { label: "Ngày tặng", value: "createdAt" },
  ];
  const extraButtons = [
    <BasicButtonPrimary
      key="create-gift"
      onclick={onCreateGift}
      icon={<PlusOutlined />}
      className="d-flex align-items-center gap-1"
      text="Tặng quà"
    />,
    ...actionButtons,
  ];

  return (
    <div className="gift-order-table">
    <DataTable
      rowKey="orderId"
      columns={columns}
      data={tableData}
      loading={loading}
      pagination={pagination}
      filters={filters}
      sorter={sorter}
      handleTableChange={handleTableChange}
      hasMore={hasMore}
      refetch={refetch}
      extraButtons={extraButtons}
      onSelectedRows={() => {}}
      isFilterByDates
      onSearch={(value) => handleSearch(value)}
      startDate={startDate}
      endDate={endDate}
      columnStorageKey={columnStorageKey}
      showFilter={false}
      showSelection={false}
      sortOptions={giftSortOptions}
    />
    </div>
  );
};

export default GiftOrderTable;
